// libraries
import { useGridApiRef } from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import InlineConfirmationPanel from 'ext-ui/components/InlineConfirmationPanel/InlineConfirmationPanel'
import NotesInputField from 'ext-ui/components/NotesInputField/NotesInputField'
import Pill from 'ext-ui/components/Pill/Pill'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { buildSplitExpenseCategoriesFromDetails } from 'src/app/TransactionDetail/TransactionDetail'
import { selectUploadingAttachmentsFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import AttachmentsList from 'src/components/AttachmentsList/AttachmentsList'
import ErrorList from 'src/components/ErrorList/ErrorList'
import { ExpenseCategoriesContext } from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import ExpenseCategoriesInputs from 'src/components/ExpenseCategoriesInputs/ExpenseCategoriesInputs'
import { EXPENSE_CATEGORY_EMPTY_OPTION } from 'src/components/ExpenseCategoryDropdown/ExpenseCategoryDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgAddReceipt from 'src/components/Svgs/SvgAddReceipt'
import SvgSplit from 'src/components/Svgs/SvgSplit'
import SvgUnsplit from 'src/components/Svgs/SvgUnsplit'
import SplitExpenseCategoriesDataGrid from 'src/components/TransactionsDataGrid/SplitExpenseCategoriesDataGrid'
import { TransactionExpenseCategorySplit } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import RejectionNotice from 'src/feature/ManagerReview/RejectionNotice/RejectionNotice'
import { useFormFields } from 'src/hooks'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  ConnectedPlatformStatus,
  ExpenseDetailsModel,
  TransactionDetailModel,
  TransactionManagerRejectionReason,
  TransactionManagerReviewStatus,
  TransactionStatus,
} from 'src/models/transaction.models'
import { activityUrl } from 'src/navigation/urls'
import { useFetchTaskDetails, usePatchTask } from 'src/queries/activity.queries'
import { useQueryActiveTransactionIntegration } from 'src/queries/integrations/orgIntegrations.queries'
import { useQueryAdditionalFields } from 'src/queries/organizations/additionalFields.queries'
import {
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentFailureAction,
  uploadAttachments,
  UploadAttachmentSuccessAction,
} from 'src/redux/actions/data/receiptAttachments.actions'
import { LocationState } from 'src/redux/createRootReducer'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectOrganizationReceiptMgmtIsLoadedFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import {
  selectActiveOrgIntegration,
  selectOrgIntegrationsListResponsesIsLoadedFromState,
} from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectTransactionSplitsWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toDollars } from 'src/utils/currency.utils'

import style from './TaskDetails.style'

function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

interface TaskDetailsProps {
  id: string
}

const TaskDetails: React.FC<TaskDetailsProps> = (props) => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const dispatch = useDispatch()
  const history = useHistory<LocationState>()

  const { data: liveData, isLoading } = useFetchTaskDetails(props.id)

  const [data, setData] = useState(liveData)

  const [patched, setPatched] = useState(false)

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()
  const {
    show_transaction_splits,
    show_expense_requirements: showAdditionalFields,
  } = useSelector(selectFeatureFlaggerFromState)

  // Split functionality
  const transactionSplitsWhitelist = useSelector(
    selectTransactionSplitsWhitelistFromRemoteConfigState,
  )
  const { data: user } = useQueryOwnUser({})
  const hasSaasFeatures = useHasSaasFeatures()
  const showTransactionSplits =
    (show_transaction_splits ||
      transactionSplitsWhitelist?.includes(user?.organizationId || '')) &&
    hasSaasFeatures

  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(props.id)

  const [splits, setSplits] = useState<TransactionExpenseCategorySplit[]>([])
  const [showSplit, setShowSplit] = useState(
    showTransactionSplits && (data?.isSplit ?? false),
  )
  const [confirmUnsplitting, setConfirmUnsplitting] = useState(false)
  const [localSplitError, setLocalSplitError] = useState<string>()

  const apiRef = useGridApiRef()

  const isSplitLocked =
    activeTransactionIntegration?.syncStatus === ConnectedPlatformStatus.Success

  const taskIsRejectedForSpenderAction = useMemo(() => {
    return (
      managerReviewRulesEnabled &&
      data?.managerReviewStatus === TransactionManagerReviewStatus.Rejected &&
      [
        TransactionManagerRejectionReason.MissingReceipt,
        TransactionManagerRejectionReason.IncorrectReceipt,
        TransactionManagerRejectionReason.MissingExpenseCategories,
        TransactionManagerRejectionReason.IncorrectExpenseCategories,
        TransactionManagerRejectionReason.MissingNote,
      ].includes(
        data?.managerRejectionReason as TransactionManagerRejectionReason,
      )
    )
  }, [
    managerReviewRulesEnabled,
    data?.managerReviewStatus,
    data?.managerRejectionReason,
  ])

  // check for required receipts that are missing
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const isReceiptManagementLoaded = useSelector(
    selectOrganizationReceiptMgmtIsLoadedFromState,
  )
  const receiptsMissing =
    !!receiptManagementEnabled &&
    data?.attachmentsCount === 0 &&
    data?.receiptRequired

  // check for required exp cats that are missing
  const { data: mandatoryExpenseDetails, isPending: isPendingExpCats } =
    useQueryExpenseCategories({
      filters: { active: true, requiredForTransactions: true },
    })
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const isActiveIntegrationLoaded = useSelector(
    selectOrgIntegrationsListResponsesIsLoadedFromState,
  )
  const expCatsMissing = useMemo(() => {
    return (
      data?.isCleared &&
      data?.checkMissingRequiredExpenseCategory(
        activeIntegration?.connectedPlatform,
        mandatoryExpenseDetails,
      )
    )
  }, [data, activeIntegration, mandatoryExpenseDetails])

  // Fetch additional fields data
  const { data: mandatoryAdditionalFields } = useQueryAdditionalFields({
    filters: { active: true, required: true },
  })
  const additionalFieldsMissing =
    data?.isCleared &&
    !!mandatoryAdditionalFields?.some((field) => {
      const fieldDetails = Object.values(data?.additionalFields || {})?.find(
        (txnFields) => txnFields.expenseAdditionalFieldId === field.id,
      )?.details
      return fieldDetails === undefined
    })

  const { data: expCatSchema } = useQueryExpenseCategories({
    filters: { active: true },
  })
  const { data: additionalFieldsSchema } = useQueryAdditionalFields({
    filters: { active: true },
  })

  const initialExpCatFields = useMemo((): Record<
    string,
    Array<ExpenseDetailsModel | undefined>
  > => {
    const expDetails = data?.expenseDetails?.map((expCat) => [expCat])
    return expDetails !== undefined
      ? expDetails.reduce((acc, curr) => {
          const expCat =
            curr.length > 0
              ? curr.filter((cat): cat is ExpenseDetailsModel => !!cat)[0]
              : undefined
          const isCustomerOrVendor =
            expCat?.categoryType === ExpenseCategoryType.Customer ||
            expCat?.categoryType === ExpenseCategoryType.Vendor
          const schemaEC = isCustomerOrVendor
            ? expCatSchema?.find((ec) => ec.type === expCat?.categoryType)
            : undefined
          const schemaId = isCustomerOrVendor && schemaEC ? schemaEC.id : ''
          if (expCat) {
            return {
              ...acc,
              // use schemaId for vendor/customer (found via type) since those dont have categoryIds
              [expCat.categoryId || schemaId]: curr,
            }
          } else {
            return acc
          }
        }, {})
      : {}
  }, [data?.expenseDetails, expCatSchema])
  const explicitSelectNone = false // idk

  const expCatFieldValidation = (): Record<
    string,
    Array<(val?: string) => React.ReactElement | undefined>
  > => {
    return {}
  }

  const additionalFieldValidation = (): Record<
    string,
    Array<(val?: string) => React.ReactElement | undefined>
  > => {
    return {}
  }

  const formatBaseValuesToSelected = (
    vals: Record<string, Array<ExpenseDetailsModel | undefined>>,
  ): Record<string, string | undefined> => {
    return (
      expCatSchema?.reduce((acc, curr) => {
        // map multiple values to undefined so that only 1 option appears selected in the dropdown
        const baseVal =
          vals?.[curr.id] !== undefined && vals?.[curr.id].length === 1
            ? Array.from(vals?.[curr.id])[0]
            : undefined
        return {
          ...acc,
          // use labelName if no id, for free text values
          [curr.id]: baseVal?.labelId || baseVal?.labelName,
        }
      }, {}) || {}
    )
  }

  const baseExpCats = useMemo(
    () => formatBaseValuesToSelected(initialExpCatFields),
    [initialExpCatFields, expCatSchema],
  )

  const baseAdditionalFields = useMemo(() => {
    return (
      data?.additionalFields?.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.expenseAdditionalFieldId]: curr.details,
        }
      }, {}) || {}
    )
  }, [data?.additionalFields, additionalFieldsSchema])

  const {
    fields: expCatFields,
    setFields: setExpCatFields,
    fieldsErrors: expCatFieldsErrors,
    onFieldChange: onExpCatFieldChange,
  } = useFormFields<Record<string, string | undefined>>(
    {},
    expCatFieldValidation,
  )

  const { fields: additionalFields, setFields: setAdditionalFields } =
    useFormFields<Record<string, string | undefined>>(
      {},
      additionalFieldValidation,
    )

  const isTaskLoaded = !!data && !isLoading

  const shareDepartmentAcrossSplits = useMemo(
    () =>
      [
        'QUICKBOOKS_ONLINE',
        'QUICKBOOKS_DESKTOP',
        'QUICKBOOKS_ONLINE_SANDBOX',
      ].includes(activeIntegration?.connectedPlatform ?? ''),
    [activeIntegration?.connectedPlatform],
  )

  useEffect(() => {
    if (isTaskLoaded) {
      if (expCatSchema) {
        setExpCatFields(baseExpCats)
        if (data?.isSplit) {
          // set intitial value for splits
          setSplits(
            buildSplitExpenseCategoriesFromDetails({
              expenseDetails: data?.expenseDetails,
              totalAmountCents: data?.clearingOrAuthAmountCents ?? 0,
              isSplit: data?.isSplit,
              expCatSchema,
              shareDepartmentAcrossSplits,
            }),
          )
        }
      }
      setAdditionalFields(baseAdditionalFields)
    }
  }, [props.id, isTaskLoaded])

  const [uploadedAttachments, setUploadedAttachments] = useState<
    ReceiptAttachmentModel[]
  >(data?.receiptAttachments || [])

  // notes
  const [notes, setNotes] = useState<string>(data?.notes || '')

  useEffect(() => {
    if (isTaskLoaded) {
      setUploadedAttachments(data?.receiptAttachments || [])
      setNotes(data?.notes || '')
    }
  }, [isTaskLoaded])

  // Split validation functions
  const getSplitAmountValue = useCallback(
    (split: TransactionExpenseCategorySplit): number => {
      const value = split.splitValue
      if (value === undefined || value === null) return 0
      const numeric =
        typeof value === 'number' ? value : Number.parseFloat(String(value))
      return Number.isFinite(numeric) ? numeric : 0
    },
    [],
  )

  const hasValidSplitAmount = useCallback(
    (split: TransactionExpenseCategorySplit): boolean =>
      getSplitAmountValue(split) > 0,
    [getSplitAmountValue],
  )

  const hasExpenseCategorySelection = useCallback(
    (split: TransactionExpenseCategorySplit): boolean => {
      return Object.values(split.expenseCategories ?? {}).some((mapping) => {
        const candidate = mapping.labelId ?? mapping.labelName
        return candidate !== undefined && candidate !== null
          ? String(candidate).trim() !== ''
          : false
      })
    },
    [],
  )

  const hasIncompleteSplit = useCallback(
    (targetSplits: TransactionExpenseCategorySplit[]): boolean =>
      targetSplits.some(
        (split) =>
          !hasValidSplitAmount(split) || !hasExpenseCategorySelection(split),
      ),
    [hasExpenseCategorySelection, hasValidSplitAmount],
  )

  // Convert first split row to regular expense category format
  const convertFirstSplitToExpCatFields = useCallback((): Record<
    string,
    string | undefined
  > => {
    if (splits.length === 0) return {}

    const firstSplit = splits[0]
    const convertedFields: Record<string, string | undefined> = {}

    Object.entries(firstSplit.expenseCategories || {}).forEach(
      ([categoryId, mapping]) => {
        if (mapping.labelId || mapping.labelName) {
          // Use labelId if available, otherwise use labelName (for free text)
          convertedFields[categoryId] = mapping.labelId || mapping.labelName
        }
      },
    )

    return convertedFields
  }, [splits])

  // Split-related effects
  useEffect(() => {
    if (!showTransactionSplits) {
      setShowSplit(false)
      setConfirmUnsplitting(false)
      return
    }
    setShowSplit(data?.isSplit ?? false)
  }, [showTransactionSplits, data?.id, data?.isSplit])

  useEffect(() => {
    if (!localSplitError) return
    if (!hasIncompleteSplit(splits)) {
      setLocalSplitError(undefined)
    }
  }, [hasIncompleteSplit, localSplitError, splits])

  // attachments
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const [attachmentError, setAttachmentError] = useState<
    string | React.ReactElement | undefined
  >()
  const dispatchUploadAttachments = async (
    attachments: PostReceiptAttachmentRequest[],
  ): Promise<void> => {
    const response = await dispatch(uploadAttachments(attachments) as any)
    const successfulResponses = response.filter(
      (resp: UploadAttachmentFailureAction | UploadAttachmentSuccessAction) =>
        resp.type === UPLOAD_ATTACHMENT_SUCCESS,
    ) as UploadAttachmentSuccessAction[]
    setUploadedAttachments((prev) => [
      ...prev,
      ...successfulResponses.map((successful) => successful.payload),
    ])
  }

  // patch task
  const {
    mutate: patchTask,
    deleteTaskAndUpdateCounts: deleteTaskAndUpdateCounts,
    isPending: patchIsPending,
    errors: patchErrors,
  } = usePatchTask()

  const errors = [...patchErrors, ...(localSplitError ? [localSplitError] : [])]

  const queryClient = useQueryClient()
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPatched(true)

    let expenseDetails = Object.fromEntries(
      Object.entries(expCatFields)
        .map(([catId, labelId]) => {
          const ec = expCatSchema?.find((ec) => ec.id === catId)

          if (
            // props.explicitSelectNone &&
            ec?.freeTextAllowed &&
            !ec?.hasLabels &&
            !labelId
          ) {
            // TODO: is free text empty unchanged or set to nothing?
            if (initialExpCatFields[catId]?.length > 1) {
              // if we would show "Multiple Values", treat empty as unchanged
              return [catId, undefined]
            } else {
              // if they are all the same or there are none to begin with, treat empty as empty
              return [catId, undefined]
            }
          }
          if (labelId === EXPENSE_CATEGORY_EMPTY_OPTION) {
            return [catId, undefined]
          }
          return [catId, labelId]
        })
        .filter(
          (categoryLabelMap): categoryLabelMap is [string, string] =>
            !!categoryLabelMap[1],
        ),
    )
    let appliedExpenseDetails = Object.entries(expenseDetails)
      .filter((keyVal) => !!keyVal[1])
      .map(([key, val]) => ({ categoryId: key, labelId: val }))
    let appliedAdditionalFields = Object.entries(additionalFields)
      .filter((keyVal) => !!keyVal[1])
      .map(([key, val]) => ({ expenseAdditionalFieldId: key, details: val }))

    if (showSplit && hasIncompleteSplit(splits)) {
      setLocalSplitError(
        translate(
          'One or more of the splits above is incomplete. You must include an amount and at least one expense category selection.',
        ),
      )
      return
    }

    if (data === undefined) return

    try {
      const response = await patchTask({
        transactionId: data.id,
        attachments: uploadedAttachments,
        expenseDetails: !showSplit ? appliedExpenseDetails : undefined,
        additionalFields: appliedAdditionalFields,
        notes: notes,
        splits: showSplit ? splits : undefined,
      })

      if (response) {
        const transaction = new TransactionDetailModel(response)

        const checkRejectionReason =
          managerReviewRulesEnabled &&
          transaction?.managerReviewStatus ===
            TransactionManagerReviewStatus.Rejected

        const receiptsMissing =
          !!receiptManagementEnabled &&
          transaction?.attachmentsCount === 0 &&
          transaction?.receiptRequired
        const receiptActionRequired =
          receiptsMissing ||
          (checkRejectionReason &&
            [
              TransactionManagerRejectionReason.MissingReceipt,
              TransactionManagerRejectionReason.IncorrectReceipt,
            ].includes(
              transaction.managerRejectionReason as TransactionManagerRejectionReason,
            ))

        const expDetailsMissing =
          transaction.checkMissingRequiredExpenseCategory(
            activeIntegration?.connectedPlatform,
            mandatoryExpenseDetails,
          )
        const expCatActionRequired =
          expDetailsMissing ||
          (checkRejectionReason &&
            [
              TransactionManagerRejectionReason.MissingExpenseCategories,
              TransactionManagerRejectionReason.IncorrectExpenseCategories,
            ].includes(
              transaction.managerRejectionReason as TransactionManagerRejectionReason,
            ))

        const additionalFieldsMissing =
          transaction?.isCleared &&
          !!mandatoryAdditionalFields?.some((field) => {
            const fieldDetails = Object.values(
              transaction?.additionalFields || {},
            )?.find(
              (txnFields) => txnFields.expenseAdditionalFieldId === field.id,
            )?.details
            return fieldDetails === undefined
          })
        const additionalFieldsActionRequired =
          (showAdditionalFields && additionalFieldsMissing) ||
          (checkRejectionReason &&
            [TransactionManagerRejectionReason.MissingNote].includes(
              transaction.managerRejectionReason as TransactionManagerRejectionReason,
            ))

        if (
          !receiptActionRequired &&
          !expCatActionRequired &&
          !additionalFieldsActionRequired
        ) {
          // if the task is complete, remove the task from the task list and update the counts
          deleteTaskAndUpdateCounts(transaction.id)
          history.replace({
            pathname: activityUrl(),
            search: `?tab=tasks`,
          })
        } else {
          // onSuccess
          queryClient.setQueryData(
            transactionsQueryKeys.detail(transaction.id),
            transaction,
          )
          setPatched(false)
        }
      }
    } catch (err) {
      setPatched(false)
    }
  }

  useEffect(() => {
    if (
      !patched &&
      liveData &&
      JSON.stringify(liveData) !== JSON.stringify(data)
    ) {
      setData(liveData)
    }
  }, [liveData, data, patched])

  const isAllLoaded =
    data &&
    isReceiptManagementLoaded &&
    !isPendingExpCats &&
    isActiveIntegrationLoaded

  const requiredActions = useMemo(() => {
    return {
      receipts:
        receiptsMissing ||
        (taskIsRejectedForSpenderAction &&
          [
            TransactionManagerRejectionReason.MissingReceipt,
            TransactionManagerRejectionReason.IncorrectReceipt,
          ].includes(
            data?.managerRejectionReason as TransactionManagerRejectionReason,
          )),
      expCats:
        expCatsMissing ||
        (taskIsRejectedForSpenderAction &&
          [
            TransactionManagerRejectionReason.MissingExpenseCategories,
            TransactionManagerRejectionReason.IncorrectExpenseCategories,
          ].includes(
            data?.managerRejectionReason as TransactionManagerRejectionReason,
          )),
      notes:
        !showAdditionalFields &&
        (false ||
          (taskIsRejectedForSpenderAction &&
            [TransactionManagerRejectionReason.MissingNote].includes(
              data?.managerRejectionReason as TransactionManagerRejectionReason,
            ))),
      additionalFields:
        showAdditionalFields &&
        (additionalFieldsMissing ||
          (taskIsRejectedForSpenderAction &&
            [TransactionManagerRejectionReason.MissingNote].includes(
              data?.managerRejectionReason as TransactionManagerRejectionReason,
            ))),
    }
  }, [
    receiptsMissing,
    expCatsMissing,
    additionalFieldsMissing,
    taskIsRejectedForSpenderAction,
    data?.managerRejectionReason,
    showAdditionalFields,
  ])

  const formHasMissingReceipts = useMemo((): boolean => {
    return receiptsMissing && uploadedAttachments.length === 0
  }, [receiptsMissing, uploadedAttachments.length])

  const formHasMissingExpCats = useMemo((): boolean => {
    const remainingUnfilledRequiredExpCats =
      mandatoryExpenseDetails?.filter((requiredExpCat) => {
        return expCatFields[requiredExpCat.id] === undefined
      }) || []
    return remainingUnfilledRequiredExpCats.length > 0
  }, [mandatoryExpenseDetails, expCatFields])

  const formHasMissingNotes = useMemo((): boolean => {
    return notes.length === 0
  }, [notes])

  const formHasMissingAdditionalDetails = useMemo((): boolean => {
    const remainingUnfilledRequiredAdditionalFields =
      mandatoryAdditionalFields?.filter((requiredField) => {
        return (
          additionalFields[requiredField.id] === undefined ||
          additionalFields[requiredField.id] === ''
        )
      }) || []
    return remainingUnfilledRequiredAdditionalFields.length > 0
  }, [mandatoryAdditionalFields, additionalFields])

  // steph
  useEffect(() => {
    // if everything is loaded
    if (isAllLoaded) {
      if (
        Object.values(requiredActions).filter((action) => action).length === 0
      ) {
        // this transaction has no tasks
        // redirect the user to the tasks tab
        history.replace({
          pathname: activityUrl(),
          search: `?tab=tasks`,
        })
      }
    }
  }, [isAllLoaded, requiredActions, history])

  if (isLoading || !data) {
    return <LoadingBackgroundPage />
  }

  return (
    <form css={style} className={'TaskDetails'} onSubmit={onSubmit}>
      {taskIsRejectedForSpenderAction && (
        <RejectionNotice transaction={data} variant={'long'} />
      )}
      <div className={'TaskDetails-transactionDetails'}>
        <div className={'TaskDetails-transactionDetails-title'}>
          {data.merchantName}
        </div>
        <div className={'TaskDetails-transactionDetails-amount'}>
          <div className={'TaskDetails-transactionDetails-amount-value'}>
            {currencyFormatter({
              amount: toDollars(data.clearingBillingAmountCents || 0),
              currency: data.clearingBillingCurrency,
              forceHideCurrency: true,
            })}
          </div>
          <div className={'TaskDetails-transactionDetails-amount-currency'}>
            {data.clearingBillingCurrency}
          </div>
        </div>
        <div className={'TaskDetails-transactionDetails-details'}>
          {toTitleCase(data.mccGroup || '')}
          <span className={'separator'}>{'•'}</span>
          {moment(data.clearedAt).format('L[,] LT')}
          <span className={'separator'}>{'•'}</span>
          <span className={'TaskDetails-transactionDetails-details-creditCard'}>
            {data.creditCardDisplayName}
            <Pill size={'small'}>{data.vcnLast4}</Pill>
          </span>
        </div>
      </div>
      {requiredActions.receipts && (
        <div className={'TaskDetails-missingReceipts'}>
          <div className={'TaskDetails-missingReceipts-title'}>
            {translate('Receipts')}
            {formHasMissingReceipts && (
              <Pill size={'small'} color={'severe'}>
                {translate('Missing')}
              </Pill>
            )}
          </div>
          <AttachmentsList
            attachments={uploadedAttachments}
            showNewStyling
            uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
            uploadAttachments={dispatchUploadAttachments}
            deleteAttachment={(attachmentId: string): void => {
              setUploadedAttachments((prev) =>
                prev.filter((attachment) => attachment.id !== attachmentId),
              )
            }}
            isReceiptRequired={data?.receiptRequired}
            setAttachmentError={setAttachmentError}
            attachmentsText={
              <>
                <SvgAddReceipt />
                <span className={'TaskDetails-missingReceipts-noReceipts'}>
                  {translate('+ Add Receipt')}
                </span>
              </>
            }
          />
          {attachmentError && (
            <ErrorList
              className={'TaskDetails-missingReceipts-errorList'}
              errors={[attachmentError]}
            />
          )}
        </div>
      )}
      {requiredActions.expCats && (
        <div className={'TaskDetails-missingExpCats'}>
          <div className={'TaskDetails-missingExpCats-title'}>
            {translate('Expense Categories')}
            {formHasMissingExpCats && (
              <Pill size={'small'} color={'severe'}>
                {translate('Missing')}
              </Pill>
            )}
            {showTransactionSplits && !isSplitLocked && (
              <div style={{ marginLeft: 'auto' }}>
                <Button
                  className={cn('split-button', { selected: showSplit })}
                  variant={'secondary'}
                  dropShadow={false}
                  disabled={data?.status === TransactionStatus.Pending}
                  disabledTooltip={translate(
                    'Pending transactions cannot be split',
                  )}
                  onClick={(): void => {
                    if (showSplit) {
                      if (data?.isSplit) {
                        setConfirmUnsplitting(true)
                        return
                      }
                      setShowSplit(false)
                      return
                    }
                    setShowSplit(true)
                  }}
                >
                  {showSplit ? <SvgUnsplit /> : <SvgSplit />}
                  <span>
                    {showSplit ? translate('Undo Split') : translate('Split')}
                  </span>
                </Button>
              </div>
            )}
          </div>
          {confirmUnsplitting && data?.isSplit && (
            <InlineConfirmationPanel
              message={translate(
                'Are you sure you want to unsplit this transaction? You will lose your splits and need to recode them if you decide to split again.',
              )}
              confirmLabel={translate('Confirm Undo')}
              onCancel={() => setConfirmUnsplitting(false)}
              onConfirm={() => {
                // Convert first split row to regular expense category format
                const firstSplitFields = convertFirstSplitToExpCatFields()
                // eslint-disable-next-line no-console
                console.log('firstSplitFields', firstSplitFields)
                setExpCatFields(firstSplitFields)

                // Clear split state
                setConfirmUnsplitting(false)
                setShowSplit(false)
                setSplits([])
                setLocalSplitError(undefined)
              }}
            />
          )}
          {showTransactionSplits && showSplit && (
            <>
              <SplitExpenseCategoriesDataGrid
                apiRef={apiRef}
                transactionId={data?.id}
                splits={splits}
                onChange={setSplits}
                totalAmountCents={data?.clearingOrAuthAmountCents}
                isLocked={isSplitLocked}
              />
            </>
          )}
          {(!showTransactionSplits || !showSplit) && (
            <ExpenseCategoriesInputs
              context={ExpenseCategoriesContext.TRANSACTION}
              initialExpDetailsMultiple={initialExpCatFields}
              expenseDetailsErrors={expCatFieldsErrors}
              expenseDetails={expCatFields}
              onExpenseCategoryChange={(
                catId: string,
                labelId: string,
              ): void => {
                onExpCatFieldChange(catId, labelId)
              }}
              deselectExpenseCategory={
                explicitSelectNone
                  ? undefined
                  : (catId: string): void => {
                      const newExpenseCategories = { ...expCatFields }
                      delete newExpenseCategories[catId]
                      setExpCatFields(newExpenseCategories)
                    }
              }
              explicitSelectNone={explicitSelectNone}
              enforceRequired={false}
            />
          )}
        </div>
      )}
      {requiredActions.notes && (
        <div className={'TaskDetails-notes'}>
          <div className={'TaskDetails-notes-title'}>
            <span>{translate('Notes')}</span>
            {formHasMissingNotes && (
              <Pill size={'small'} color={'severe'}>
                {translate('Missing')}
              </Pill>
            )}
          </div>
          <NotesInputField
            baseValue={data.notes}
            value={notes}
            onChange={setNotes}
            placeholder={translate('Add a quick note...')}
            maxLength={2000}
            notesCharacters={notes.length ?? 0}
            variant={notes.length === 0 ? 'severe' : undefined}
          />
        </div>
      )}
      {requiredActions.additionalFields && (
        <div className={'TaskDetails-additionalFields'}>
          <div className={'TaskDetails-additionalFields-title'}>
            <span>{translate('Additional Fields')}</span>
            {formHasMissingAdditionalDetails && (
              <Pill size={'small'} color={'severe'}>
                {translate('Missing')}
              </Pill>
            )}
          </div>
          {additionalFieldsSchema?.map((field) => (
            <TextInput
              key={field.id}
              variant={'medium'}
              state={
                field.required &&
                data.isCleared &&
                (additionalFields[field.id] === undefined ||
                  additionalFields[field.id] === '')
                  ? 'error'
                  : undefined
              }
              label={field.name}
              placeholder={translate('Type here')}
              requirement={field.required ? 'required' : undefined}
              value={additionalFields[field.id]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value
                setAdditionalFields({
                  ...additionalFields,
                  [field.id]: newValue,
                })
              }}
            />
          ))}
        </div>
      )}
      {errors.length > 0 && (
        <ErrorList className={'TaskDetails-errorList'} errors={errors} />
      )}
      <div>
        <Button
          type={'submit'}
          loading={patchIsPending}
          data-testid={'submit-btn'}
        >
          {translate('Submit')}
        </Button>
      </div>
    </form>
  )
}

export default TaskDetails
